'use client';

import React, { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { useRefreshTokenMutation, useAuthenticateUserMutation } from 'src/redux/services/centralApi';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { User } from 'src/interfaces/entities/user';
import { toast } from 'react-toastify';
import { checkRole } from 'src/common/helpers/checkRole';

interface ISignIn {
	email: string
	password: string
}

type AuthContextType = {
	isAuthenticated: boolean
	user: User | null
	signIn: (auth: ISignIn) => Promise<void>
	signOut: () => void
	refreshToken: () => Promise<void>
	loadingAuth: boolean
	loadingRefresh: boolean
	authError: boolean
	updateAuthData: (token: string, user: User) => void
};

export const AuthContext = createContext({} as AuthContextType);

export default function AuthProvider({ children, }: PropsWithChildren) {
	const [ refreshToken, refreshTokenStatus, ] = useRefreshTokenMutation();
	const [ authenticateUserMutation, authenticateUserStatus, ] = useAuthenticateUserMutation();
	const navigate = useNavigate();

	const [ user, setUser, ] = useState<User | null>(null);
	const isAuthenticated = !!user;

	const updateAuthData = (token: string, user: User) => {
		console.log('Updating auth data with token:', token, 'and user:', user);
		setUser(user);

		if (checkRole('super_admin', { user, }) || checkRole('partner', { user, })) {
			Cookies.set('intraguard.super_token', token);
		}
		Cookies.set('intraguard.token', token);
	};

	const signOut = () => {
		setUser(null);
		localStorage.clear();
		Cookies.remove('intraguard.token');
		Cookies.remove('intraguard.super_token');

		navigate('/login');
	};

	const signIn = async ({ email, password, }: ISignIn) => {
		await authenticateUserMutation({ email, password, })
			.unwrap()
			.then((response) => {
				//@ts-ignore
				if (response?.payload?.token && response?.payload?.user) {
					//@ts-ignore
					const user = response.payload.user;
					//@ts-ignore
					const token = response.payload.token;
					updateAuthData(token, user);
					navigate('/');
				}
			})
			.catch((err) => {
				const errorMessage = err?.data?.frontend_message || 'Erro ao autenticar. Por favor, tente novamente.';
				toast.error(errorMessage);
			});
	};

	const refresh = async () => {
		const superToken = Cookies.get('intraguard.super_token');
		const token = Cookies.get('intraguard.token');

		if (!token || !superToken) {
			signOut();
			return;
		}

		await refreshToken({ token: superToken || token, })
			.unwrap()
			.then((response) => {
				console.log({ response, });
				//@ts-ignore
				if (response?.payload?.token && response?.payload?.user) {
					//@ts-ignore
					const user = response.payload.user;
					//@ts-ignore
					const token = response.payload.token;
					updateAuthData(token, user);
				}
			})
			.catch(() => {
				signOut();
			});
	};

	useEffect(() => {
		if (
			!window.location.pathname.startsWith('/cadastro') &&
			!window.location.pathname.startsWith('/login') &&
			!window.location.pathname.startsWith('/partners')
		)
			refresh();
	}, []);

	const contextValue = {
		user,
		signIn,
		signOut,
		refreshToken: refresh,
		isAuthenticated,
		loadingAuth: authenticateUserStatus.isLoading,
		authError: authenticateUserStatus.isError,
		loadingRefresh: refreshTokenStatus.isLoading,
		updateAuthData,
	};

	return (
		<AuthContext.Provider value={contextValue}>
			{children}
		</AuthContext.Provider>
	);
}
